'use client';
import { ComponentProps, FC, useState } from 'react';
import Button, { IButtonProps } from '@/shared/ui/Button/Button';
import ShareIcon from '~/icons/share.svg';
import WrapperIcon from '@/shared/ui/WrapperIcon/WrapperIcon';
import DefaultPopover from '../DefaultPopover/DefaultPopover';
import { cva } from 'class-variance-authority';
import VkIcon from '~/icons/vk.svg';
import OkIcon from '~/icons/ok.svg';
import TgIcon from '~/icons/tg.svg';
import WhatsappIcon from '~/icons/whatsapp.svg';
import DropdownLink from '@/entities/DropdownLink/DropdownLink';
import { useLocalizedPathname } from '@/shared/i18n/routing';
import { useGetGlobalOptions } from '@/shared/hooks/useGetGlobalOptions';
import { useLocale } from 'next-intl';
import XIcon from '~/icons/x.svg';
import LinkedinIcon from '~/icons/linkedin.svg';
import { useGetShareLinks } from '@/shared/hooks/useGetShareLinks';
import clsx from 'clsx';

interface Props extends Pick<IButtonProps, 'size'> {
  //Url для шаринга; по умолчанию - url текущей страницы
  shareUrl?: string;
  withoutText?: boolean;
  className?: string;
  buttonClassName?: string | ((isOpen: boolean) => string);
  popoverClassName?: string;
  popoverPlacement?: ComponentProps<typeof DefaultPopover>['placement'];
}

const ShareButton: FC<Props> = ({
  shareUrl,
  withoutText,
  className,
  buttonClassName,
  popoverClassName,
  size,
  popoverPlacement,
}) => {
  const locale = useLocale();

  const { data: globalOptions } = useGetGlobalOptions({ lang: locale });

  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const pathname = useLocalizedPathname();

  const { data, isLoading } = useGetShareLinks(
    {
      url: shareUrl ?? process.env.NEXT_PUBLIC_FRONT_BASE_URL + pathname,
      lang: locale,
    },
    {
      enabled: isOpen,
    }
  );

  return (
    <DefaultPopover
      className={popoverClassName}
      buttonClassName={className}
      placement={popoverPlacement}
      isOpen={isOpen && !isLoading}
      onOpen={() => setIsOpen(() => !isLoading)}
      onClose={() => {
        setIsOpen(false);
        setCopied(false);
      }}
      render={
        <div>
          {data?.vk ? (
            <DropdownLink
              className={cvaLink()}
              innerClassName={cvaLinkInner()}
              active={false}
              item={{
                id: 0,
                title: globalOptions?.settings_share.button_vk_text ?? '',
                icon: <VkIcon className={'w-2.4 h-2.4'} />,
              }}
              href={data.vk}
              target={'_blank'}
              rel={'nofollow noreferrer'}
            />
          ) : null}
          {data?.ok ? (
            <DropdownLink
              className={cvaLink()}
              innerClassName={cvaLinkInner()}
              active={false}
              item={{
                id: 0,
                title: globalOptions?.settings_share.button_ok_text ?? '',
                icon: <OkIcon className={'w-2.4 h-2.4'} />,
              }}
              href={data.ok}
              target={'_blank'}
              rel={'nofollow noreferrer'}
            />
          ) : null}
          {data?.telegram ? (
            <DropdownLink
              className={cvaLink()}
              innerClassName={cvaLinkInner()}
              active={false}
              item={{
                id: 0,
                title: globalOptions?.settings_share.button_telegram_text ?? '',
                icon: <TgIcon className={'w-2.4 h-2.4'} />,
              }}
              href={data.telegram}
              target={'_blank'}
              rel={'nofollow noreferrer'}
            />
          ) : null}
          {data?.whatsapp ? (
            <DropdownLink
              className={cvaLink()}
              innerClassName={cvaLinkInner()}
              active={false}
              item={{
                id: 0,
                title: globalOptions?.settings_share.button_whatsapp_text ?? '',
                icon: <WhatsappIcon className={'w-2.4 h-2.4'} />,
              }}
              href={data.whatsapp}
              target={'_blank'}
              rel={'nofollow noreferrer'}
            />
          ) : null}
          {data?.x ? (
            <DropdownLink
              className={cvaLink()}
              innerClassName={cvaLinkInner()}
              active={false}
              item={{
                id: 0,
                title: 'X',
                icon: <XIcon className={'w-2.4 h-2.4'} />,
              }}
              href={data.x}
              target={'_blank'}
              rel={'nofollow noreferrer'}
            />
          ) : null}
          {data?.linkedin ? (
            <DropdownLink
              className={cvaLink()}
              innerClassName={cvaLinkInner()}
              active={false}
              item={{
                id: 0,
                title: 'LinkedIn',
                icon: <LinkedinIcon className={'w-2.4 h-2.4'} />,
              }}
              href={data.linkedin}
              target={'_blank'}
              rel={'nofollow noreferrer'}
            />
          ) : null}

          <Button
            className={'mt-1.2 text-1.6-600'}
            size={'sm'}
            theme={'primary'}
            stretched
            onClick={() => {
              navigator.clipboard
                .writeText(
                  shareUrl ?? process.env.NEXT_PUBLIC_FRONT_BASE_URL + pathname
                )
                .then(() => setCopied(true));
            }}>
            {copied
              ? globalOptions?.settings_share.button_copied_text
              : globalOptions?.settings_share.button_copy_text}
          </Button>
        </div>
      }>
      {withoutText ? (
        <WrapperIcon theme={isOpen ? 'active' : 'blurred'}>
          <ShareIcon className={cvaIcon({ isOpen })} />
        </WrapperIcon>
      ) : (
        <Button
          theme={isOpen ? 'focus' : 'blurred'}
          className={clsx(
            cvaButton({ isOpen }),
            typeof buttonClassName === 'function'
              ? buttonClassName(isOpen)
              : buttonClassName
          )}
          icon={<ShareIcon className={cvaIcon({ isOpen })} />}
          size={size || 'xs'}>
          {globalOptions?.settings_share.button_text}
        </Button>
      )}
    </DefaultPopover>
  );
};

const cvaLink = cva([
  'ShareButton-cvaLink',
  'flex items-center gap-0.8',
  'font-semibold',
]);

const cvaLinkInner = cva(['ShareButton-cvaLinkInner', 'w-full']);

const cvaButton = cva(
  ['ShareButton-cvaButton', 'transition-colors duration-300'],
  {
    variants: {
      isOpen: {
        true: '!text-cPurple300',
        false: 'text-cWhite',
      },
    },
  }
);

const cvaIcon = cva(
  ['ShareButton-cvaIcon', 'w-2.4 h-2.4', 'transition-colors duration-300'],
  {
    variants: {
      isOpen: {
        true: 'text-cPurple300',
        false: 'text-cWhite',
      },
    },
  }
);

export default ShareButton;
